import * as moment from 'moment';
import * as React from 'react';
import { getTranslate, Translate } from 'react-localize-redux';
import PopupDeletion from '../../../../../components_v2/popup/PopupDeletion';
import storeLang from '../../../../../helpers/storeLang';
import { Loader } from '../../../../../styles/global/css/GlobalLoader';
import { GreenSidely, RedSidely } from '../../../../../styles/global/css/Utils';
import { UserToolTip } from '../../../../../styles/global/ToolTip';
import { LoadingStateEnum } from '../../../../import/model';
import { formatCurrency } from '../../../../reports/utils';
import { deleteCompanyOpportunity } from '../../../data/action';
import { CompanyOpportunity } from '../../../model/Model';
import { NoDataText } from '../../../style/Style';
import LinkedElementCard, { CardAvatar, CardDeleteButton } from '../LinkedElementCard';
import { CardList, CardProbability, SeeMore } from '../style/LinkedElementStyle';
import Restricted from '../../../../permissions/Restricted';
import { LinkedElementId } from '../../../../../containers/companies/components/linkedElements/actions';
import { getLinkedElementOpportunities } from '../actions';
import OpportunityPopup from '../../../../opportunities/popup/OpportunityPopup';

interface DeletionState {
	isOpen: boolean
	opportunity?: CompanyOpportunity
}

function Opportunity(props: {
	id: LinkedElementId
	isExtend?: boolean
	newCreation?: CompanyOpportunity
	onDelete: () => void
}): JSX.Element {

	const translate = getTranslate(storeLang.getState().localize);

	const DEFAULT_LIMIT = 3;

	const [loading, setLoading] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADING);
	const [loadingMore, setLoadingMore] = React.useState<LoadingStateEnum>(LoadingStateEnum.LOADED);
	const [opportunities, setOpportunities] = React.useState<CompanyOpportunity[]>([]);
	const [total, setTotal] = React.useState<number>(0);
	const [offset, setOffset] = React.useState<number>(0);

	const [deletionState, setDeletionState] = React.useState<DeletionState>({ isOpen: false });
	const [openedId, setOpenedId] = React.useState<number>();

	React.useEffect(() => {
		setLoading(LoadingStateEnum.LOADING);
		getLinkedElementOpportunities({
			id: props.id,
			limit: DEFAULT_LIMIT,
			offset
		}).then(response => {
			console.log(response);
			setOpportunities(response.opportunities);
			setTotal(response.total);
			setLoading(LoadingStateEnum.LOADED);
		});
	}, []);

	React.useEffect(() => {
		if (props.newCreation != null) {
			setOpportunities([props.newCreation, ...opportunities]);
			setOffset(offset + 1);
		}
	}, [props.newCreation]);

	function addMore(): void {
		const newOffset = offset + DEFAULT_LIMIT;
		setOffset(newOffset);
		setLoadingMore(LoadingStateEnum.LOADING);
		getLinkedElementOpportunities({
			id: props.id,
			limit: DEFAULT_LIMIT,
			offset: newOffset
		}).then(response => {
			setOpportunities(opportunities.concat(response.opportunities));
			setTotal(response.total > 0 ? response.total : total);
			setLoadingMore(LoadingStateEnum.LOADED);
		});
	}

	function confirmationDeletionOpportunity(): void {
		if (!deletionState.opportunity) {
			return;
		}

		deleteCompanyOpportunity({
			client_company_id: deletionState.opportunity.client_company_id,
			id: deletionState.opportunity.opportunity_id
		}).then(res => {
			if (res) {
				props.onDelete();
				setOpportunities(opportunities.filter(o => o.opportunity_id !== deletionState.opportunity?.opportunity_id));
				setOffset(offset - 1);
				setTotal(total - 1);
			}
		});
	}

	function getColor(proba: number): string {
		const p = `${Math.floor(proba / 10.0)}0`;
		switch (p) {
			case '0':
				return '#BABABA';
			case '10':
				return RedSidely;
			case '20':
				return '#FF814B';
			case '30':
				return '#FEA240';
			case '40':
				return '#FEC940';
			case '50':
				return '#FFEA2C';
			case '60':
				return '#E2EE5C';
			case '70':
				return '#D2F34E';
			case '80':
				return '#87F16D';
			case '90':
				return '#3CDF87';
			case '100':
				return GreenSidely;

			default:
				return '#BABABA';
		}
	}

	return (
		(<CardList>
			{
				loading === LoadingStateEnum.LOADING
					? <Loader center width="30px" />
					: opportunities.length > 0
						? opportunities.map(o => (
							<Restricted to={{ objectAction: 'ReadOpportunity' }} ownerId={o.incharge_id} key={`Opportunity[${o.opportunity_id}]`}>
								<LinkedElementCard
									isExtend={props.isExtend}
									color={o.opportunity_status_color}
									onClick={() => setOpenedId(o.opportunity_id)}
									avatar={<UserToolTip userId={o.incharge_id} left='-35px'>
										<CardAvatar photo={o.incharge_photo} name={o.incharge_name.trim()} userId={o.incharge_id} />
									</UserToolTip>}
									title={o.opportunity_title}
									lines={[`${moment.utc(o.opportunity_created_at).local().format('DD/MM/YYYY')} · ${formatCurrency(o.opportunity_revenue)}`, o.opportunity_status_name]}
									actions={<>
										<CardProbability backgroundColor={getColor(o.opportunity_probability)}>{o.opportunity_probability.toString().split('.')[0]}</CardProbability>
										<Restricted to={{ objectAction: 'DeleteOpportunity' }} ownerId={o.incharge_id}>
											<CardDeleteButton onClick={() => setDeletionState({ ...deletionState, isOpen: true, opportunity: o })} />
										</Restricted>
									</>}
								/>
							</Restricted>
						))
						: <NoDataText><Translate id='no_opportunities' /></NoDataText>
			}
			{
				loading === LoadingStateEnum.LOADED && opportunities.length < total &&
				<SeeMore onClick={() => addMore()}><Translate id='see_more' /></SeeMore>
			}
			{
				loading === LoadingStateEnum.LOADED && loadingMore === LoadingStateEnum.LOADING &&
				<Loader centerHorizontal width="30px" />
			}
			<OpportunityPopup isOpen={openedId !== undefined} setOpen={isOpen => !isOpen && setOpenedId(undefined)} opportunityId={openedId} />
			<PopupDeletion
				isOpen={deletionState.isOpen}
				records={0}
				translationKey=''
				customTitle={translate('company.detail.linked_elements.opportunity').toString()}
				customMessage={translate('global.fire.delete_message').toString().replace('{{NAME}}', `'${deletionState.opportunity?.opportunity_title ?? ''}'`)}
				onClickOut={() => setDeletionState({ ...deletionState, isOpen: false })}
				onValidation={() => confirmationDeletionOpportunity()}
				noVerification
			/>
		</CardList>)
	);
}

export default Opportunity;
